import { makeArticle, saintRelated } from "./learnFactory";

export const pageSaintHowFastVsTms = makeArticle({
  slug: "how-fast-does-saint-tms-work-compared-to-tms",
  title: "How Fast Does SAINT® Work Compared to TMS? | Neuro Wellness",
  description:
    "How fast SAINT® TMS works compared to conventional TMS: 5 days vs 4-6 weeks, when improvement may be noticed, research outcomes, and individual response.",
  canonical: "/learn/how-fast-does-saint-tms-work-compared-to-tms",
  h1: "How Fast Does SAINT® TMS Work Compared to Conventional TMS?",
  breadcrumbLabel: "How Fast SAINT® Works vs TMS",
  shortAnswer:
    "SAINT® TMS is delivered over 5 consecutive days, while conventional TMS is delivered over 4–6 weeks. In the SAINT® clinical trials, remission was measured at 4 weeks after treatment, with 78.6% of the active group reaching remission versus 13.3% of the sham group. In the open-label study, the average time to remission was 2.6 days. However, 'how fast it works' refers to the treatment schedule and research outcomes — individual response varies, and no outcome is guaranteed. Never promise results in five days.",
  keyTakeaways: [
    "SAINT® is delivered in 5 days; conventional TMS takes 4–6 weeks.",
    "The SAINT® RCT measured remission at 4 weeks: 78.6% active vs 13.3% sham.",
    "Open-label data showed average time to remission of 2.6 days.",
    "'How fast' refers to the protocol schedule and research outcomes, not guaranteed individual results.",
    "Individual response varies; no outcome is guaranteed.",
  ],
  sections: [
    {
      heading: "Treatment Duration: 5 Days vs 4–6 Weeks",
      paragraphs: [
        "The most obvious difference in speed is the treatment schedule. SAINT® delivers 50 sessions over 5 consecutive days. Conventional TMS delivers 30–36 sessions over 4–6 weeks, one session per day, five days a week.",
        "This means the SAINT® treatment course is completed in one week, while conventional TMS takes about six weeks. For patients who cannot take 6 weeks off work or who are in a severe episode and cannot wait, this timeline difference is significant.",
      ],
    },
    {
      heading: "When Improvement May Be Noticed",
      paragraphs: [
        "In the SAINT® open-label study (Cole et al., 2020), the average time to remission was 2.6 days — meaning some patients saw improvement during the treatment week itself. In the randomized controlled trial (Cole et al., 2022), remission was measured at 4 weeks after treatment, with 78.6% of the active group reaching remission versus 13.3% of the sham group.",
        "For conventional TMS, improvement is typically noticed gradually over the 4–6 week course, with full assessment after the course is completed.",
        "It is important to understand that these are research outcomes describing groups, not guarantees for any individual. 'How fast it works' refers to the protocol schedule and published study results — individual response varies.",
      ],
    },
    {
      heading: "Research Outcomes",
      paragraphs: ["The SAINT® clinical trials reported:"],
      list: [
        "Open-label study (2020): 90.5% remission (19 of 21 completers), average time to remission 2.6 days. No sham comparison.",
        "Double-blind RCT (2022): 78.6% remission in the active group vs 13.3% in the sham group at 4 weeks. Small sample (29 analyzed), single site.",
        "Conventional TMS: pivotal RCT showed active TMS superior to sham; a naturalistic study reported about 58% response and 37% remission.",
      ],
      paragraphsAfter: [
        "These figures come from different study designs and populations and are not head-to-head comparisons. No trial has directly compared SAINT® to conventional TMS. Individual results vary.",
      ],
    },
    {
      heading: "What This Means for Patients",
      paragraphs: [
        "SAINT® is faster in terms of treatment schedule — 5 days versus 4–6 weeks. The research also showed rapid improvement in the trials, with average time to remission of 2.6 days in the open-label study. However, these are research outcomes, not guarantees.",
        "If speed of treatment is important to you — because of work, severity of your episode, or other factors — SAINT® may be worth discussing with a psychiatrist. Individual eligibility and expected outcomes should be discussed during a consultation.",
      ],
      links: [
        {
          to: "/learn/saint-vs-traditional-tms",
          text: "Compare SAINT® to traditional TMS in detail",
        },
        { to: "/saint", text: "Learn more about SAINT® at Neuro Wellness" },
        {
          to: "/contactus",
          text: "Request a consultation",
          suffix: " or call 954-827-9793.",
        },
      ],
    },
  ],
  faqs: [
    {
      question: "How fast does SAINT® TMS work compared to conventional TMS?",
      answer:
        "SAINT® is delivered in 5 days; conventional TMS takes 4–6 weeks. In the SAINT® open-label study, average time to remission was 2.6 days. In the RCT, 78.6% of the active group reached remission at 4 weeks vs 13.3% sham. These are research outcomes, not guarantees — individual response varies.",
    },
    {
      question: "How quickly can SAINT® work?",
      answer:
        "The SAINT® protocol is delivered over 5 consecutive days. In the open-label study, the average time to remission was 2.6 days, meaning some patients saw improvement during the treatment week. In the RCT, remission was measured at 4 weeks. Individual results vary, and no outcome is guaranteed.",
    },
    {
      question: "Does SAINT® work faster than regular TMS?",
      answer:
        "SAINT® is completed in 5 days versus 4–6 weeks for conventional TMS. The research showed rapid improvement in the SAINT® trials. However, no trial has directly compared SAINT® to conventional TMS head-to-head. The figures come from different study designs and are not directly comparable.",
    },
    {
      question: "Can SAINT® produce results in five days?",
      answer:
        "The SAINT® protocol is delivered over 5 days. In the open-label study, average time to remission was 2.6 days. However, 'results in five days' refers to the treatment schedule and research outcomes, not a guarantee for any individual. The RCT measured remission at 4 weeks. Individual results vary.",
    },
    {
      question: "How long does SAINT® treatment take?",
      answer:
        "SAINT® treatment takes 5 consecutive days. Each day includes 10 sessions of about 10 minutes with 50-minute breaks. Before treatment, a psychiatric evaluation and fMRI are required. The treatment week itself is 5 days, after which follow-up visits monitor response.",
    },
  ],
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://doi.org/10.1176/appi.ajp.2019.19070720",
    },
  ],
  relatedLinks: saintRelated,
});
